import * as React from "react";
import { makeStyles, Text, Spinner, Button } from "@fluentui/react-components";
import {
  CheckmarkCircle16Filled,
  ErrorCircle16Filled,
  ArrowSync16Regular,
} from "@fluentui/react-icons";
import { ACTION_STATUS } from "../../../shared/constants";

const useStyles = makeStyles({
  container: {
    borderRadius: "8px",
    padding: "10px 12px",
    transition: "all 0.2s ease",
  },
  processing: {
    backgroundColor: "var(--color-info-bg, #EDF7FC)",
    border: "1px solid var(--color-info, #13A3E0)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  processingText: {
    fontSize: "12px",
    color: "var(--color-info, #13A3E0)",
    fontWeight: "500",
  },
  completed: {
    backgroundColor: "var(--color-success-bg, #F0FAF0)",
    border: "1px solid var(--color-success, #7ED956)",
  },
  completedHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "6px",
  },
  completedIcon: {
    color: "var(--color-success, #7ED956)",
    fontSize: "16px",
    flexShrink: 0,
  },
  completedLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "var(--color-success, #7ED956)",
  },
  resultText: {
    fontSize: "12px",
    lineHeight: "1.5",
    color: "var(--text-secondary, #4A4F57)",
    wordBreak: "break-word",
  },
  resultDetail: {
    fontSize: "11px",
    lineHeight: "1.45",
    color: "var(--text-tertiary, #6E747D)",
    marginTop: "4px",
    display: "block",
  },
  failed: {
    backgroundColor: "var(--color-error-bg, #FFF0F0)",
    border: "1px solid var(--color-error, #FF5757)",
  },
  failedHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "6px",
  },
  failedIcon: {
    color: "var(--color-error, #FF5757)",
    fontSize: "16px",
    flexShrink: 0,
  },
  failedLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "var(--color-error, #FF5757)",
  },
  errorText: {
    fontSize: "12px",
    lineHeight: "1.45",
    color: "var(--text-secondary, #4A4F57)",
    wordBreak: "break-word",
  },
  retryRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "8px",
  },
  retryBtn: {
    minWidth: "auto",
    fontSize: "11px",
    color: "var(--color-error, #FF5757)",
  },
});

/**
 * Format result object into displayable text.
 */
function formatResult(result) {
  if (!result) return null;
  if (typeof result === "string") return result;
  if (result.message) return result.message;
  if (result.summary) return result.summary;
  // Fallback: render as JSON
  try {
    return JSON.stringify(result, null, 2);
  } catch {
    return String(result);
  }
}

/**
 * ExecutionStatusCard — Phase 2 inline result card for action execution.
 * Shows different UI per status: processing (spinner), completed (green check + result), failed (red error + Retry).
 *
 * @param {{
 *   actionId: string,
 *   status: string,
 *   result?: Object|string|null,
 *   error?: string|null,
 *   onRetry?: Function,
 * }} props
 */
const ExecutionStatusCard = ({ actionId, status, result, error, onRetry }) => {
  const styles = useStyles();

  // ── Processing ──
  if (status === ACTION_STATUS.PROCESSING) {
    return (
      <div className={`${styles.container} ${styles.processing}`}>
        <Spinner size="tiny" />
        <Text className={styles.processingText}>Executing action...</Text>
      </div>
    );
  }

  // ── Completed ──
  if (status === ACTION_STATUS.COMPLETED) {
    const resultText = formatResult(result);

    return (
      <div className={`${styles.container} ${styles.completed}`}>
        <div className={styles.completedHeader}>
          <CheckmarkCircle16Filled className={styles.completedIcon} />
          <Text className={styles.completedLabel}>Completed</Text>
        </div>
        {resultText && (
          <Text className={styles.resultText}>{resultText}</Text>
        )}
        {result && result.details && (
          <Text className={styles.resultDetail}>{result.details}</Text>
        )}
      </div>
    );
  }

  // ── Failed ──
  if (status === ACTION_STATUS.FAILED) {
    return (
      <div className={`${styles.container} ${styles.failed}`}>
        <div className={styles.failedHeader}>
          <ErrorCircle16Filled className={styles.failedIcon} />
          <Text className={styles.failedLabel}>Failed</Text>
        </div>
        <Text className={styles.errorText}>
          {error || "An unexpected error occurred. Please try again."}
        </Text>
        {onRetry && (
          <div className={styles.retryRow}>
            <Button
              className={styles.retryBtn}
              appearance="subtle"
              size="small"
              icon={<ArrowSync16Regular />}
              onClick={() => onRetry(actionId)}
            >
              Retry
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ── Pending / Unknown — render nothing ──
  return null;
};

export default ExecutionStatusCard;
